import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Menu, X, PawPrint, ShoppingBag, User as UserIcon, LogOut } from 'lucide-react';
import { Button } from './ui/Button';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { AuthModal } from './AuthModal';

gsap.registerPlugin(ScrollTrigger);

export const Navbar = () => {
  const navRef = useRef<HTMLElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const { cartCount, setIsCartOpen } = useCart();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const isShopPage = location.pathname === '/shop';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <nav 
        ref={navRef}
        className="fixed top-6 left-0 right-0 z-[60] flex items-center justify-center px-4"
      >
        <div className={`flex items-center justify-between transition-all duration-500 ease-in-out
          ${isScrolled 
            ? 'w-full max-w-2xl bg-white/80 backdrop-blur-lg border border-primary/10 shadow-[0_8px_32px_rgba(0,0,0,0.08)] rounded-full py-2 px-6 gap-4 md:gap-8' 
            : 'w-full max-w-7xl px-4 md:px-8 py-4 bg-transparent'
          }`}
        >
          <Link to="/" className={`flex items-center gap-2 font-serif text-2xl md:text-3xl transition-colors duration-300 ${isScrolled ? 'text-primary' : 'text-ink'} rotate-[-2deg]`}>
            <PawPrint className="w-6 h-6 md:w-8 md:h-8" />
            Canine Candy
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-5">
            {[
              { name: 'Shop', path: '/shop' },
              { name: 'Story', path: '/story' },
              { name: 'FAQ', path: '/faq' },
              { name: 'Contact', path: '/contact' }
            ].map((item) => (
              <Link 
                key={item.name} 
                to={item.path}
                className={`font-sans font-bold text-lg px-2 py-2 hover:-translate-y-0.5 transition-transform duration-200 ${isScrolled ? 'text-ink' : 'text-ink/80'}`}
              >
                {item.name}
              </Link>
            ))}

            <button 
              onClick={() => setIsCartOpen(true)}
              className={`relative p-3 hover:bg-black/5 rounded-full transition-colors cursor-pointer ${isScrolled ? 'text-ink' : 'text-ink'}`}
              aria-label={`Open cart, ${cartCount} items`}
            >
              <ShoppingBag className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">
                  {cartCount}
                </span>
              )}
            </button>

            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/profile" className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary/20 hover:border-primary transition-colors flex items-center justify-center bg-primary/5 cursor-pointer">
                  {user.user_metadata.avatar_url ? (
                    <img 
                      src={user.user_metadata.avatar_url} 
                      alt={user.email} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${user.user_metadata.full_name || user.email}&background=F27D26&color=fff`;
                      }}
                    />
                  ) : (
                    <img 
                      src={`https://ui-avatars.com/api/?name=${user.user_metadata.full_name || user.email}&background=F27D26&color=fff`} 
                      alt={user.email} 
                      className="w-full h-full object-cover" 
                    />
                  )}
                </Link>
                <button 
                  onClick={signOut}
                  className="p-3 hover:bg-black/5 rounded-full text-ink/80 hover:text-primary-dark transition-colors cursor-pointer"
                  title="Sign Out"
                  aria-label="Sign out"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setIsAuthModalOpen(true)}
                className="p-3 hover:bg-black/5 rounded-full text-ink/80 hover:text-primary-dark transition-colors cursor-pointer"
                title="Sign In"
                aria-label="Sign in"
              >
                <UserIcon className="w-6 h-6" />
              </button>
            )}
          </div>

          <div className={`hidden md:block ml-4 ${isShopPage ? 'invisible' : ''}`}>
            <Link to="/shop" tabIndex={isShopPage ? -1 : 0}>
              <Button 
                variant={isScrolled ? 'primary' : 'primary'}
                className="scale-90 shadow-md"
                tabIndex={isShopPage ? -1 : 0}
              >
                Order Treats
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button 
              onClick={() => setIsCartOpen(true)}
              className="relative p-3 text-ink cursor-pointer"
              aria-label={`Open cart, ${cartCount} items`}
            >
              <ShoppingBag className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full border-2 border-white">
                  {cartCount}
                </span>
              )}
            </button>
            <button 
              className="text-ink p-3 relative z-[110] cursor-pointer flex items-center justify-center"
              onClick={(e) => {
                console.log('Burger menu clicked');
                e.preventDefault();
                e.stopPropagation();
                setIsMobileMenuOpen(true);
              }}
              aria-label="Open menu"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        ref={mobileMenuRef}
        className={`fixed inset-0 bg-background z-[100] flex flex-col items-center justify-center transition-all duration-500 ease-in-out md:hidden
          ${isMobileMenuOpen ? 'translate-x-0 opacity-100 visible pointer-events-auto' : 'translate-x-full opacity-0 invisible pointer-events-none'}`}
      >
        <button 
          className="absolute top-8 right-8 p-2 text-ink cursor-pointer"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="Close menu"
        >
          <X size={32} />
        </button>

        <div className="flex flex-col items-center gap-8">
          {[
            { name: 'Shop', path: '/shop' },
            { name: 'Story', path: '/story' },
            { name: 'FAQ', path: '/faq' },
            { name: 'Contact', path: '/contact' }
          ].map((item) => (
            <Link 
              key={item.name} 
              to={item.path}
              className="font-serif text-4xl text-ink hover:text-primary transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}
          <div className="mt-8 flex flex-col items-center gap-6">
            {user ? (
              <div className="flex flex-col items-center gap-4">
                <Link to="/profile" onClick={() => setIsMobileMenuOpen(false)} className="w-16 h-16 rounded-full overflow-hidden border-4 border-primary/20 hover:border-primary transition-colors flex items-center justify-center bg-primary/5 cursor-pointer">
                  {user.user_metadata.avatar_url ? (
                    <img 
                      src={user.user_metadata.avatar_url} 
                      alt={user.email} 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${user.user_metadata.full_name || user.email}&background=F27D26&color=fff`;
                      }}
                    />
                  ) : (
                    <img 
                      src={`https://ui-avatars.com/api/?name=${user.user_metadata.full_name || user.email}&background=F27D26&color=fff`} 
                      alt={user.email} 
                      className="w-full h-full object-cover" 
                    />
                  )}
                </Link>
                <p className="font-bold text-ink">{user.user_metadata.full_name || user.email}</p>
                <div className="flex gap-4">
                  <Link 
                    to="/profile" 
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-2 text-primary font-bold cursor-pointer"
                  >
                    <UserIcon className="w-5 h-5" /> Profile
                  </Link>
                  <button 
                    onClick={() => { signOut(); setIsMobileMenuOpen(false); }}
                    className="flex items-center gap-2 text-ink/80 hover:text-primary-dark font-bold cursor-pointer"
                  >
                    <LogOut className="w-5 h-5" /> Sign Out
                  </button>
                </div>
              </div>
            ) : (
              <button 
                onClick={() => { setIsAuthModalOpen(true); setIsMobileMenuOpen(false); }}
                className="flex items-center gap-2 text-ink/80 hover:text-primary-dark font-bold text-2xl cursor-pointer"
              >
                <UserIcon className="w-8 h-8" /> Sign In
              </button>
            )}
            
            {!isShopPage && (
              <Link to="/shop" onClick={() => setIsMobileMenuOpen(false)} className="mt-4">
                <Button 
                  variant="primary"
                  className="text-xl px-12 py-4 shadow-lg"
                >
                  Order Treats
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
      />
    </>
  );
};
